import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  CalendarDays,
  Clock,
  Plus,
  Check,
  X,
  Loader2,
  Trash2,
  AlertCircle,
} from 'lucide-react';

const todayLocal = () => new Date().toISOString().split('T')[0];

const LeavesPermissions = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isArabic = language === 'ar';
  const isAdmin = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [type, setType] = useState('leave'); // leave | permission
  const [date, setDate] = useState(todayLocal());
  const [hours, setHours] = useState(1);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/leaves');
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل التحميل' : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((i) => i.status === filter);
  }, [items, filter]);

  const submit = async () => {
    if (!date) {
      toast.error(isArabic ? 'التاريخ مطلوب' : 'Date required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leaves', {
        type,
        date,
        hours: type === 'permission' ? Number(hours) : null,
        reason: reason || '',
      });
      toast.success(isArabic ? 'تم إرسال الطلب' : 'Submitted');
      setShowForm(false);
      setReason('');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل' : 'Failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (id, approved) => {
    setBusyId(id);
    try {
      let rejection_reason = null;
      if (!approved) {
        rejection_reason = window.prompt(isArabic ? 'سبب الرفض (اختياري)' : 'Rejection reason (optional)') || '';
      }
      await api.post(`/leaves/${id}/decide`, { approved, rejection_reason });
      toast.success(approved ? (isArabic ? 'تم الاعتماد' : 'Approved') : (isArabic ? 'تم الرفض' : 'Rejected'));
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isArabic ? 'حذف الطلب؟' : 'Delete request?')) return;
    try {
      await api.delete(`/leaves/${id}`);
      toast.success(isArabic ? 'تم الحذف' : 'Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل');
    }
  };

  const typeLabel = (t, h) => {
    if (t === 'leave') return isArabic ? 'إجازة (يوم كامل)' : 'Leave (full day)';
    return isArabic ? `إذن ${h || 0} ساعات` : `Permission ${h || 0}h`;
  };

  const statusBadge = (s) => {
    const classes = s === 'approved' ? 'bg-emerald-500/20 text-emerald-500'
      : s === 'rejected' ? 'bg-red-500/20 text-red-500'
        : 'bg-yellow-500/20 text-yellow-500';
    const text = s === 'approved' ? (isArabic ? 'معتمد' : 'Approved')
      : s === 'rejected' ? (isArabic ? 'مرفوض' : 'Rejected')
        : (isArabic ? 'معلّق' : 'Pending');
    return <span className={`inline-block px-2 py-0.5 rounded text-xs ${classes}`}>{text}</span>;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
            <CalendarDays className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{isArabic ? 'الإجازات والأذونات' : 'Leaves & Permissions'}</h1>
            <p className="text-muted-foreground text-sm">
              {isArabic ? 'طلب إجازة (يوم كامل) أو إذن (ساعة/ساعتين/ثلاث ساعات) — يحتاج اعتماد المدير' : 'Request a full-day leave or 1/2/3-hour permission — needs admin approval'}
            </p>
          </div>
        </div>
        {!isAdmin && (
          <Button onClick={() => setShowForm(true)} className="gap-2" data-testid="new-leave-btn">
            <Plus className="w-4 h-4" />
            {isArabic ? 'طلب جديد' : 'New Request'}
          </Button>
        )}
      </div>

      {/* Filter chips */}
      <Card className="p-3 bg-card border-white/10 flex flex-wrap gap-2">
        {['all', 'pending', 'approved', 'rejected'].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)}
            data-testid={`leaves-filter-${f}`}
          >
            {f === 'all' && (isArabic ? 'الكل' : 'All')}
            {f === 'pending' && (isArabic ? 'معلّق' : 'Pending')}
            {f === 'approved' && (isArabic ? 'معتمد' : 'Approved')}
            {f === 'rejected' && (isArabic ? 'مرفوض' : 'Rejected')}
          </Button>
        ))}
      </Card>

      {/* List */}
      <Card className="overflow-hidden bg-card border-white/10">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline-block me-2" />
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
            {isArabic ? 'لا توجد طلبات' : 'No requests'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaves-table">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-start py-3 px-3">{isArabic ? 'الحلاق' : 'Barber'}</th>
                  <th className="text-start py-3 px-3">{isArabic ? 'النوع' : 'Type'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'التاريخ' : 'Date'}</th>
                  <th className="text-start py-3 px-3">{isArabic ? 'السبب' : 'Reason'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
                  <th className="text-center py-3 px-3">{isArabic ? 'إجراء' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-secondary/20" data-testid={`leave-row-${r.id}`}>
                    <td className="py-3 px-3 font-medium">{r.barber_name || '-'}</td>
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1">
                        {r.type === 'leave' ? <CalendarDays className="w-4 h-4 text-blue-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                        {typeLabel(r.type, r.hours)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center">{r.date}</td>
                    <td className="py-3 px-3 text-muted-foreground text-xs max-w-[240px]">
                      {r.reason || '-'}
                      {r.status === 'rejected' && r.rejection_reason && (
                        <div className="text-[10px] text-red-400 mt-1">⚠ {r.rejection_reason}</div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-center">{statusBadge(r.status)}</td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {isAdmin && r.status === 'pending' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-emerald-500" disabled={busyId === r.id} onClick={() => decide(r.id, true)} data-testid={`approve-leave-${r.id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" disabled={busyId === r.id} onClick={() => decide(r.id, false)} data-testid={`reject-leave-${r.id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {(isAdmin || (r.created_by === user?.id && r.status === 'pending')) && (
                          <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-500" onClick={() => remove(r.id)} data-testid={`delete-leave-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="leave-form-modal">
          <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full space-y-4" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">{isArabic ? 'طلب جديد' : 'New Request'}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{isArabic ? 'النوع' : 'Type'}</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={type === 'leave' ? 'default' : 'outline'}
                  size="sm"
                  className="gap-1 flex-1"
                  onClick={() => setType('leave')}
                  data-testid="type-leave-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                  {isArabic ? 'إجازة (يوم كامل)' : 'Leave (full day)'}
                </Button>
                <Button
                  type="button"
                  variant={type === 'permission' ? 'default' : 'outline'}
                  size="sm"
                  className="gap-1 flex-1"
                  onClick={() => setType('permission')}
                  data-testid="type-permission-btn"
                >
                  <Clock className="w-4 h-4" />
                  {isArabic ? 'إذن (ساعات)' : 'Permission (hours)'}
                </Button>
              </div>
            </div>

            {type === 'permission' && (
              <div>
                <label className="block text-sm font-medium mb-2">{isArabic ? 'عدد الساعات' : 'Hours'}</label>
                <div className="flex gap-2">
                  {[1, 2, 3].map((h) => (
                    <Button
                      key={h}
                      type="button"
                      variant={hours === h ? 'default' : 'outline'}
                      size="sm"
                      className="flex-1"
                      onClick={() => setHours(h)}
                      data-testid={`hours-${h}-btn`}
                    >
                      {h} {isArabic ? 'ساعة' : 'h'}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">{isArabic ? 'التاريخ' : 'Date'}</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-secondary/50 border-white/10"
                data-testid="leave-date-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{isArabic ? 'السبب (اختياري)' : 'Reason (optional)'}</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={isArabic ? 'اكتب السبب...' : 'Write your reason...'}
                className="w-full rounded-md bg-secondary/50 border border-white/10 px-3 py-2 text-sm"
                data-testid="leave-reason-input"
              />
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-2 text-xs text-yellow-500 flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>
                {isArabic
                  ? 'سيتم إرسال الطلب للمدير، وستظهر الإجازة/الإذن في تقاريرك بعد الاعتماد.'
                  : 'Your request will be sent to the manager and shown in revenue reports once approved.'}
              </span>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} disabled={submitting}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={submit} disabled={submitting} data-testid="submit-leave-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isArabic ? 'إرسال' : 'Submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeavesPermissions;
